/**
 * Textos novos das janelas de busca (proposta "Search" do Claude Design, 17/10/2026). O
 * português fica como padrão no código (chaves `sx_*` em src/i18n/locales); en, es e fr vêm dos
 * arquivos de idioma. Os rótulos dos campos continuam nas chaves antigas (keywords_places…).
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const CAPTURE_PT = {
  open_full: 'Abrir em tela cheia',
  exit_full: 'Voltar para a janela',
  tip_title: 'Agora dá para abrir em tela cheia',
  tip_text:
    'Mais espaço para escolher as fontes e acompanhar a captura. O que você preencheu vai junto.',
  tip_ok: 'Entendi',
  close: 'Fechar',
  capturing_note: 'Capturando na aba nova. Os campos ficam travados até você parar.',
  keywords_hint: 'Escreva o que você está buscando para começar.',
  sites_required: 'Escolha pelo menos um site ou rede social.',
  kw_places: 'Ex.: dentista, academia',
  kw_limit: 'Até {{n}} segmentos por busca',
  kw_limit_one: 'Um segmento por busca',
  kw_too_many: 'Use até {{n}} segmentos por busca. Tire os que sobram para começar.',
  kw_too_many_one: 'Use um segmento por busca. Tire os que sobram para começar.',
  places_limit: 'Até {{n}} locais por busca',
  places_limit_one: 'Um local por busca',
  places_too_many: 'Use até {{n}} locais por busca. Tire os que sobram para começar.',
  places_too_many_one: 'Use um local por busca. Tire os que sobram para começar.',
  sites_limit: 'Até {{n}} fontes por busca',
  sites_limit_one: 'Uma fonte por busca',
  domains_limit: 'Até {{n}} provedores por busca',
  domains_limit_one: 'Um provedor por busca',
  lists_left_one: 'Teste: resta 1 lista deste tipo',
  lists_left_other: 'Teste: restam {{n}} listas deste tipo',
  lists_left_none: 'Teste: as listas deste tipo acabaram. Veja os planos para criar mais.',
  kw_phones: 'Ex.: advogado, corretor de imóveis',
  kw_emails: 'Ex.: nutricionista, arquiteto',
  places_placeholder: 'Cidade, bairro ou estado',
  sites_placeholder: 'Ex.: instagram.com',
  domains_placeholder: 'Ex.: @gmail.com',
  country_placeholder: 'Escolha um país',
  ddd_label: 'Com qual DDD?',
  area_label: 'Com qual código de área?',
  ddd_all: 'Todos os DDDs do Brasil',
  area_all: 'Todos os códigos de área',
  ddd_region: '{{city}} e região',
  ddd_search: 'Número, cidade ou estado',
  ddd_empty: 'Nenhum código encontrado.',
  km: '{{n}} km',
  remove: 'Remover {{item}}',
  zoom_in: 'Aproximar',
  zoom_out: 'Afastar',
  // Início da captura (proposta B, 25/10/2026). A extensão publicada abre a aba com foco e não a
  // fecha no fim, então os textos não falam em grupo de abas, em "sem tirar você daqui" nem em
  // fechar sozinha.
  start_preparing: 'Preparando a busca…',
  start_opening: 'Abrindo a aba…',
  start_next: 'Em seguida, a captura abre numa aba nova. Deixe essa aba aberta até o fim.',
  start_slow_places: 'Ainda preparando. Áreas maiores levam um pouco mais.',
  start_slow: 'Ainda preparando a busca. Isso leva alguns segundos.',
  start_back: 'Voltar para a busca',
  start_title: 'A captura acontece numa aba nova',
  start_step_1: 'Ela abre na barra de abas, e o navegador vai para ela.',
  start_step_2: 'Deixe essa aba aberta: é nela que a captura acontece.',
  start_step_3: 'No fim, ela mostra quantos leads encontrou. Eles ficam em Ver leads.',
  start_ready: 'Busca pronta.',
  start_open: 'Abrir a aba e começar',
  start_open_queued: 'Abrindo quando ficar pronta…',
  start_new_tab: 'vai abrir aqui',
  start_tab_groups: 'Organizar as abas da busca',
  start_sr_preparing: 'Preparando a busca.',
  start_sr_intro:
    'A captura acontece numa aba nova. Ela abre na barra de abas, e o navegador vai para ela. Deixe essa aba aberta até o fim.',
  start_sr_opened: 'A captura começou numa aba nova.',
  start_error_prepare: 'Não deu para preparar a busca. Tente de novo.',
  start_error_open: 'Não deu para abrir a aba da captura. Tente de novo.',
} as const;

export type CaptureKey = keyof typeof CAPTURE_PT;

export function useCaptureText() {
  const { t } = useTranslation();
  const tx = useCallback(
    (key: CaptureKey, vars?: Record<string, string | number>): string => {
      const out: unknown = t(`sx_${key}`, {
        defaultValue: CAPTURE_PT[key],
        ...vars,
        interpolation: { escapeValue: false },
      });
      if (typeof out === 'string') return out;
      // Tradutor sem suporte a opções (alguns testes): cai no português com as variáveis.
      return CAPTURE_PT[key].replace(/\{\{(\w+)\}\}/g, (_m, name: string) =>
        String(vars?.[name] ?? ''),
      );
    },
    [t],
  );
  return { tx, t };
}

/** Texto puro de um rótulo antigo com HTML ("<b>Telefones</b> de…<br /><em>…</em>"). */
export function plainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Título e descrição do item de menu antigo (`module_x_menu` = título<br /><em>descrição</em>). */
export function splitMenuLabel(html: string): { title: string; description: string } {
  const [title = '', ...rest] = html.split(/<br\s*\/?>/i);
  return { title: plainText(title), description: plainText(rest.join(' ')) };
}
